import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { Macro } from '@rapoo/device-sdk'

import { useDeviceStore } from '@/stores'
import type { MacroListItem, MacroPlayMode } from '../types'
import { LOOP_MODE_TOGGLE, LOOP_MODE_HOLD } from '../types'
import { defaultReportError } from '@/composables/useErrorLogger'

const createEmptyMacro = ({
  index,
  name,
  mode = 1,
}: {
  index: number
  name: string
  mode?: MacroPlayMode
}): Macro => ({
  name,
  index,
  loop: mode === 1 ? 1 : mode === 2 ? LOOP_MODE_TOGGLE : LOOP_MODE_HOLD,
  package: [],
  ignoreDelay: false,
})

/* ---- 模块级单例状态，所有调用方共享 ---- */
const loading = ref(false)
const macroList = ref<MacroListItem[]>([])

/**
 * 统一管理宏列表与宏详情。
 * 通过设备 API（keyboardKeys feature）读写宏数据。
 */
export function useMacroRepository() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const keyboardKeys = computed(() => currentDevice.value?.features.keyboardKeys)

  // 防重入：每次调用递增 loadId，stream 回调中检查是否过期
  let loadId = 0

  // 流式加载宏文件列表，逐批更新 macroList
  const loadMacroList = async (): Promise<void> => {
    const feature = keyboardKeys.value
    if (!feature) {
      macroList.value = []
      return
    }

    const currentLoadId = ++loadId
    loading.value = true
    macroList.value = []

    const seenIndices = new Set<number>()

    try {
      for await (const macro of feature.getMacroListStream()) {
        // 如果期间触发了新的 loadMacroList，放弃本次流
        if (currentLoadId !== loadId) return

        for (const item of macro) {
          if (item.index == null) continue
          if (item.index < 0 || item.index > 128) continue
          // 按 index 去重，防止协议层重复推送
          if (seenIndices.has(item.index)) continue
          seenIndices.add(item.index)

          macroList.value.push({
            name: item.name,
            index: item.index as number,
          })
        }
      }
    } catch (error) {
      if (currentLoadId !== loadId) return
      console.error('[TabMacro] loadMacroList failed:', error)
      defaultReportError(`[TabMacro] loadMacroList failed: ${JSON.stringify(error)}`)
      macroList.value = []
    } finally {
      if (currentLoadId === loadId) {
        loading.value = false
      }
    }
  }

  const readMacroByIndex = async (index: number): Promise<Macro> => {
    const feature = keyboardKeys.value
    if (!feature) {
      return createEmptyMacro({ index, name: `Macro${index + 1}` })
    }

    try {
      const macro = await feature.getMacroByIndex(index)
      return {
        ...createEmptyMacro({ index, name: macro.name || `Macro${index + 1}` }),
        ...macro,
        index,
      }
    } catch (error) {
      console.error('[TabMacro] readMacroByIndex failed:', error)
      defaultReportError(`[TabMacro] readMacroByIndex failed: ${JSON.stringify(error)}`)
      return createEmptyMacro({ index, name: `Macro${index + 1}` })
    }
  }

  const saveMacroByIndex = async (macro: Macro, index: number) => {
    const normalized: Macro = {
      ...createEmptyMacro({ index, name: macro.name || `Macro${index + 1}` }),
      ...macro,
      index,
    }

    const feature = keyboardKeys.value
    if (!feature) return

    try {
      await feature.setMacroByIndex(normalized, index)
    } catch (error) {
      console.error('[TabMacro] saveMacroByIndex failed:', error)
      defaultReportError(`[TabMacro] saveMacroByIndex failed: ${JSON.stringify(error)}`)
    }
  }

  const createMacro = async (payload: MacroListItem): Promise<Macro> => {
    const macro = createEmptyMacro({ index: payload.index, name: payload.name })
    await saveMacroByIndex(macro, payload.index)
    return macro
  }

  const renameMacro = async ({ index, name }: MacroListItem): Promise<Macro> => {
    const current = await readMacroByIndex(index)
    const renamed = {
      ...current,
      index,
      name,
    }
    await saveMacroByIndex(renamed, index)
    return renamed
  }

  const deleteMacro = async (index: number) => {
    const feature = keyboardKeys.value
    if (!feature) return

    const empty = {
      ...createEmptyMacro({ index, name: '' }),
      package: [],
    } as Macro

    try {
      await feature.setMacroByIndex(empty, index)
    } catch (error) {
      console.error('[TabMacro] deleteMacro failed:', error)
      defaultReportError(`[TabMacro] deleteMacro failed: ${JSON.stringify(error)}`)
    }
  }

  return {
    loading,
    macroList,
    loadMacroList,
    readMacroByIndex,
    saveMacroByIndex,
    createMacro,
    renameMacro,
    deleteMacro,
  }
}
